import type { ReactNode } from 'react';

import { DatePicker } from '@playbook/modules/date-picker';
import { Dropdown } from '@playbook/modules/dropdown';

import css from './ExpiryPicker.module.css';

/** The API caps a key's life at a year, and reads zero as "the longest you allow". */
const LongestAllowed = '0' as const;
const PickADate = 'custom' as const;

const DaySeconds = 24 * 60 * 60;

/** The API refuses an expiry further out than this. */
const MaxLifetimeDays = 365 as const;

const Options = [
	{ value: LongestAllowed, label: 'In 12 months' },
	{ value: String(90 * DaySeconds), label: 'In 90 days' },
	{ value: String(30 * DaySeconds), label: 'In 30 days' },
	{ value: PickADate, label: 'Pick a date…' },
];

/**
 *	Whether the chosen expiry is one the API will take.
 *
 *	Only a picked date can be wrong — the presets are computed from now. The
 *	DatePicker takes no bounds of its own, so the range is checked here rather
 *	than being discovered as a 400.
 */
export function isExpiryValid(choice: string, expiresAt: number): boolean {
	if (choice !== PickADate) {
		return true;
	}

	const now = Math.floor(Date.now() / 1000);

	return expiresAt > now && expiresAt <= now + MaxLifetimeDays * DaySeconds;
}

interface ExpiryPickerProps {
	/** Unix seconds, or zero for the longest life the API allows. */
	expiresAt: number;
	/** Which option is showing, so "Pick a date…" survives an empty date. */
	choice: string;

	onChange: (choice: string, expiresAt: number) => void;
}

export function ExpiryPicker({
	expiresAt,
	choice,

	onChange,
}: ExpiryPickerProps): ReactNode {
	function chooseOption(value: string): void {
		if (value === LongestAllowed || value === PickADate) {
			onChange(value, 0);

			return;
		}

		onChange(value, Math.floor(Date.now() / 1000) + Number(value));
	}

	return (
		<div className={css.picker}>
			<Dropdown
				name="expiry"
				label="Expires"
				placeholder="Choose"
				value={choice}
				options={Options}
				help="A year is the longest the API allows."
				onValueChange={chooseOption}
			/>

			{choice === PickADate && (
				<DatePicker
					required
					name="expiresAt"
					label="Expiry date"
					placeholder="Choose a date"
					previousLabel="Previous month"
					nextLabel="Next month"
					todayLabel="Today"
					value={expiresAt ? toISODate(expiresAt) : undefined}
					help={`Between tomorrow and ${MaxLifetimeDays} days out.`}
					onValueChange={(value) => onChange(PickADate, fromISODate(value))}
				/>
			)}

			{expiresAt > 0 && !isExpiryValid(choice, expiresAt) && (
				<p className={css.invalid}>
					Pick a date in the future, no more than {MaxLifetimeDays} days
					from now.
				</p>
			)}
		</div>
	);
}

function toISODate(unixSeconds: number): string {
	return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

/** End of the chosen day, so a key picked for today still works today. */
function fromISODate(date: string): number {
	return Math.floor(new Date(`${date}T23:59:59Z`).getTime() / 1000);
}
